import * as React from "react";
import { CheckboxGroup } from "../../../../../../Shared/CheckboxGroup/CheckboxGroup";
import { SelectEditor } from "../../../../../../../react-core/Html/SelectEditor";
import { DatePicker } from "../../../../../../../react-core/Datepicker/DatePicker";
import { BackOfficeProvider } from "../../../../../../../Wrap/Models/Models/BackOfficeIntegration/BackOfficeProvider";
import { BackOfficeProviderSettings } from "../../../../../../../Wrap/Models/Models/Company/BackOfficeProviderSettings";
import { BulkContractEnquiryFrequency } from "../../../../../../../Wrap/Models/Models/Company/BulkContractEnquiryFrequency";
import { JsonDateParser } from "../../../../../../../react-core/Utils/JsonDateParser";
import { BulkEnquiryFileFormatArrayMap } from "../../../Resource/Content";
import { ICheckboxEditorProps } from "../../../../../../../react-core/Html/CheckboxEditor";
import { ContentDatePicker } from "../../../../../../Shared/Entities/Constants/DateConstants";
import { Alert } from "../../../../../../Shared/Components/Alert";
import { AlertType } from "../../../../../../Shared/Entities/Constants/AlertConstants";
import { IProcessComponentProps, ProcessComponent } from "../../../../../../Shared/Process/Process/ProcessComponent";
import { BulkContractEnquiry } from "../../../../../../../Wrap/Models/Models/Company/BulkContractEnquiry";
import { FrequencyType } from "../../../../../../../Wrap/Constants/Enums/Domain/FrequencyType";
import { AcornCommissionSettings } from "../../../../../../../Wrap/Models/Models/Company/AcornCommissionSettings";

interface IBackOfficeSettingsProps extends IProcessComponentProps {
    isReview?: boolean;
    acornCommissionSettings?: AcornCommissionSettings;
    data: BackOfficeProviderSettings;
    providers: BackOfficeProvider[];
    onSave: (changes: BackOfficeProviderSettings) => void;
    onCancel: () => void;
    onRef: (ref) => void;
}

interface IBackOfficeSettingsState {
    settings: BackOfficeProviderSettings;
    contractEnquiry: BulkContractEnquiry;
    validationMssage: string[];
}

type DateTypeOfBOProvider = keyof Pick<BulkContractEnquiry, "StartDate" | "LastDate">;

export class BackOfficeSettings extends ProcessComponent<IBackOfficeSettingsProps, IBackOfficeSettingsState> {
    constructor(props: any) {
        super(props);

        this.state = {
            settings: props.data,
            validationMssage: [],
            contractEnquiry: props.data.BulkContractEnquiry,
        };
    }

    componentDidMount(): void {
        super.componentDidMount();
        if (!this.props.data?.BulkContractEnquiry?.StartDate) {
            this.onSetBOProviderDate(new Date(), "StartDate");
        }
    }

    componentWillReceiveProps(nextProps: Readonly<IBackOfficeSettingsProps>, nextContext: any): void {
        if (nextProps.acornCommissionSettings?.Frequency) {
            this.onSetBOProviderDate(JsonDateParser.toDate(this.state.contractEnquiry?.StartDate), "StartDate");
        }
    }

    isValid(): boolean {
        const { settings } = this.state;
        this.setState({ validationMssage: [] });
        if (!settings?.BulkContractEnquiry || settings.BackOfficeProviderId <= 0) {
            this.setState({
                validationMssage: ["Please select a back office provider service."],
            });
            return false;
        }
        if (
            settings.BulkContractEnquiry.LastDate <= settings.BulkContractEnquiry.StartDate
        ) {
            this.setState({
                validationMssage: [
                    "Please make sure next date is later than start date and latest date is later than next date.",
                ],
            });
            return false;
        }
        return super.isValid();
    }

    private checkboxGroupOption(data: BulkContractEnquiryFrequency, type: string): ICheckboxEditorProps {
        return {
            label: type,
            value: data[type],
            disabled: type == "Sat" || type == "Sun",
            onChange: (v) => {
                const { settings } = this.state;
                settings.BulkContractEnquiry.Frequency[type] = v;
                this.setState({ settings: settings });
            },
        };
    }

    render() {
        const { data, providers } = this.props;

        const freq = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

        const { settings, validationMssage, contractEnquiry } = this.state;

        return (
            <>
                {validationMssage.map((m, i) => (
                    <Alert key={i} alertType={AlertType.Error} message={m}></Alert>
                ))}
                <SelectEditor
                    label="Back office system"
                    selected={data?.BackOfficeProviderId}
                    options={providers ? providers.map((x) => x.ProviderId) : []}
                    getText={(v) => {
                        return providers?.find((x) => x.ProviderId == v).BackOfficeProviderDisplayName;
                    }}
                    disabled={this.props.isReview}
                    required="Please select back office system"
                    placeholder="Please select"
                    onChange={(v) => {
                        settings.BackOfficeProviderId = v;
                        this.setState({ settings: settings });
                    }}
                />
                <SelectEditor
                    label="Bulk contract enquiry"
                    selected={settings.BulkContractEnquiry?.IsEnabled ? "Yes" : "No"}
                    options={["Yes", "No"]}
                    getText={(v) => {
                        return v;
                    }}
                    disabled={this.props.isReview}
                    required="Please select if bulk contract enquiry"
                    placeholder="Please select"
                    onChange={(v) => {
                        if (v == "Yes") {
                            contractEnquiry.IsEnabled = true;
                            settings.BulkContractEnquiry = contractEnquiry;
                            this.setState({ settings: settings });
                        } else {
                            contractEnquiry.IsEnabled = false;
                            settings.BulkContractEnquiry = contractEnquiry;
                            this.setState({ settings: settings });
                        }
                    }}
                />
                {data?.BackOfficeProviderId && settings?.BulkContractEnquiry && (
                    <>
                        <SelectEditor
                            label="File format"
                            selected={data.BulkContractEnquiry?.FileFormat}
                            options={BulkEnquiryFileFormatArrayMap.options}
                            getText={(v) => {
                                return BulkEnquiryFileFormatArrayMap.optionsMap[v];
                            }}
                            disabled={this.props.isReview}
                            onChange={(v) => {
                                contractEnquiry.FileFormat = v;
                                settings.BulkContractEnquiry = contractEnquiry;

                                this.setState({ contractEnquiry: contractEnquiry, settings: settings });
                            }}
                        />
                        <CheckboxGroup
                            type="block"
                            options={freq.map((ele) => {
                                return this.checkboxGroupOption(data.BulkContractEnquiry?.Frequency, ele);
                            })}
                        />
                        <DatePicker
                            disabled={this.props.isReview}
                            atLeast={{ value: new Date(), errorMessage: ContentDatePicker.CannotBeInThePast }}
                            selected={JsonDateParser.toDateNoOffset(data.BulkContractEnquiry.StartDate)}
                            required="Please select start date"
                            onChange={(v) => this.onSetBOProviderDate(v, "StartDate")}
                            label="Start date"
                        />

                        <DatePicker
                            disabled={this.props.isReview}
                            label="Last date"
                            atLeast={{
                                value: new Date((data.BulkContractEnquiry.StartDate as {}) as string),
                                errorMessage: ContentDatePicker.CannotBeInThePast,
                            }}
                            selected={JsonDateParser.toDateNoOffset(data.BulkContractEnquiry.LastDate)}
                            onChange={(v) => this.onSetBOProviderDate(v, "LastDate")}
                        />
                    </>
                )}
                {this.props.isReview == false && (
                    <button className="btn secondary" onClick={() => this.onSaveBackOfficeProvider()}>
                        Save
                    </button>
                )}
                {
                    <button className="btn secondary" onClick={() => this.onCancelBackOfficeProvider()}>
                        Cancel
                    </button>
                }
            </>
        );
    }
    onCancelBackOfficeProvider(): void {
        this.props.onCancel();
    }
    onSaveBackOfficeProvider() {
        if (this.isValid()) {
            this.props.onSave(this.state.settings);
        }
    }
    // when set date for back office provider
    onSetBOProviderDate(date: Date, t: DateTypeOfBOProvider) {
        const { acornCommissionSettings } = this.props;
        const { settings, contractEnquiry } = this.state;

        const frequency = acornCommissionSettings?.Frequency || FrequencyType.Weekly;
        contractEnquiry[t] = JsonDateParser.toJsonDateNoOffset(date);

        // when change start date, next date will be changed too
        if (t === "StartDate" && frequency) {
            enum FrequencyValueType {
                week,
                month,
            }
            const getDay = (year: number, month: number) => {
                while (month > 12) {
                    year += 1;
                    month -= 12;
                }
                return new Date(year, month, 0).getDate();
            };

            // count how many days when frequency is week or month
            const FrequencyValueMap: Partial<Record<FrequencyType, [FrequencyValueType, number]>> = {
                [FrequencyType.Weekly]: [FrequencyValueType.week, 1],
                [FrequencyType.Fortnightly]: [FrequencyValueType.week, 2],
                [FrequencyType.Monthly]: [FrequencyValueType.month, 1],
                [FrequencyType.Quarterly]: [FrequencyValueType.month, 3],
                [FrequencyType.HalfYearly]: [FrequencyValueType.month, 6],
                [FrequencyType.Yearly]: [FrequencyValueType.month, 12],
            };
            const frequencyTuple = FrequencyValueMap[frequency];

            let daysNumber = 0;
            switch (frequencyTuple[0]) {
                case FrequencyValueType.week:
                    daysNumber += frequencyTuple[1] * 7;
                    break;
                case FrequencyValueType.month:
                    const currentDate = {
                        year: date.getFullYear(),
                        month: date.getMonth() + 1,
                        day: date.getDate(),
                    };
                    for (let i = 1; i <= frequencyTuple[1]; i++) {
                        const endMonthDays = getDay(currentDate.year, currentDate.month + i);
                        if (i === frequencyTuple[1]) {
                            // change it to the last day if over than current month
                            const startMonthDays = getDay(currentDate.year, currentDate.month);
                            daysNumber +=
                                currentDate.day > endMonthDays
                                    ? endMonthDays + startMonthDays - currentDate.day
                                    : startMonthDays;
                        } else {
                            daysNumber += endMonthDays;
                        }
                    }
                    break;

                default:
                    break;
            }
        }

        settings.BulkContractEnquiry = contractEnquiry;
        this.setState({ contractEnquiry: contractEnquiry, settings: settings });
    }
}
